import React from 'react';
import classNames from 'classnames';

import './Resizable.scss';
import { chainedRef } from 'utils';

function ResizableHandle({ side, x, y, onDrag }) {
  const pressed = React.useRef();
  const onPointerDown = React.useCallback(e => {
    e.target.setPointerCapture(e.pointerId);
    pressed.current = true;
    onDrag({x: e.clientX * x, y: e.clientY * y});
  }, [onDrag, x, y]);

  const onPointerUp = React.useCallback(e => {
    e.target.releasePointerCapture(e.pointerId);
    pressed.current = false;
    onDrag(null);
  }, [onDrag]);

  const onPointerMove = React.useCallback(e => {
    if (pressed.current) {
      onDrag({x: e.clientX * x, y: e.clientY * y});
    }
  }, [onDrag, x, y]);

  return <div className={`ui-Resizable-handle ui-${side}`}  onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerMove={onPointerMove}/>;
}

export default React.forwardRef(function Resizable({ elementType: Element = "div", side, className, style, children, width, setWidth, height, setHeight, ...props }, ref) {
  const [size, setSize] = React.useState(null);

  if (width == null && size) width = size.width;
  if (height == null && size) height = size.height;

  const n = side.includes("n"), e = side.includes("e"), s = side.includes("s"), w = side.includes("w");
  const horz = e || w;
  const vert = n || s;

  const dragRef = React.useRef();
  const nodeRef = React.useRef();
  const onDrag = React.useCallback(pos => {
    if (pos) {
      let width = nodeRef.current.offsetWidth;
      let height = nodeRef.current.offsetHeight;
      if (dragRef.current) {
        const style = {};
        const computed = window.getComputedStyle(nodeRef.current);
        if (horz) {
          const minWidth = parseInt(computed.minWidth);
          const maxWidth = parseInt(computed.maxWidth);
          style.width = pos.x - dragRef.current.x;
          if (!isNaN(minWidth)) style.width = Math.max(style.width, minWidth);
          if (!isNaN(maxWidth)) style.width = Math.min(style.width, maxWidth);
        }
        if (vert) {
          const minHeight = parseInt(computed.minHeight);
          const maxHeight = parseInt(computed.maxHeight);
          style.height = pos.y - dragRef.current.y;
          if (!isNaN(minHeight)) style.height = Math.max(style.height, minHeight);
          if (!isNaN(maxHeight)) style.height = Math.min(style.height, maxHeight);
        }
        if (setWidth) setWidth(style.width);
        if (setHeight) setHeight(style.height);
        setSize(style);
      } else {
        dragRef.current = {x: pos.x - width, y: pos.y - height};
      }
    } else {
      dragRef.current = null;
    }
  }, [horz, vert, setWidth, setHeight]);

  const mergedRef = React.useMemo(() => chainedRef(nodeRef, ref), [nodeRef, ref]);

  return (
    <Element className={classNames("ui-Resizable", className)} ref={mergedRef} style={{...style, width, height}} {...props}>
      {children}
      {n && <ResizableHandle side="n" x={0} y={-1} onDrag={onDrag}/>}
      {e && <ResizableHandle side="e" x={1} y={0} onDrag={onDrag}/>}
      {s && <ResizableHandle side="s" x={0} y={1} onDrag={onDrag}/>}
      {w && <ResizableHandle side="w" x={-1} y={0} onDrag={onDrag}/>}
      {n && e && <ResizableHandle side="ne" x={1} y={-1} onDrag={onDrag}/>}
      {n && w && <ResizableHandle side="nw" x={-1} y={-1} onDrag={onDrag}/>}
      {s && e && <ResizableHandle side="se" x={1} y={1} onDrag={onDrag}/>}
      {s && w && <ResizableHandle side="sw" x={-1} y={1} onDrag={onDrag}/>}
    </Element>
  );
});
